let associationDateFormat: 'dmy' | 'mdy' = 'mdy'
let associationTimeZone = 'UTC'

export function setAssociationDateFormat(format: 'dmy' | 'mdy' | undefined) {
  associationDateFormat = format === 'dmy' ? 'dmy' : 'mdy'
  displayDateTimeFormatters.clear()
}

export function setAssociationTimeZone(timeZone: string | undefined) {
  associationTimeZone = timeZone || 'UTC'
}
const displayTimeFormatter = new Intl.DateTimeFormat('en-US', {
  hour: 'numeric',
  minute: '2-digit',
})

const displayDateTimeFormatters = new Map<string, Intl.DateTimeFormat>()

export function displayDate(value: string | null) {
  if (!value) return '-'

  const [year, month, day] = value.split('-').map((part) => Number(part))
  if (!year || !month || !day) return value

  const options =
    associationDateFormat === 'dmy'
      ? { day: '2-digit' as const, month: '2-digit' as const, year: 'numeric' as const }
      : { day: 'numeric' as const, month: 'short' as const, year: 'numeric' as const }

  return new Intl.DateTimeFormat(associationDateFormat === 'dmy' ? 'en-GB' : 'en-US', {
    ...options,
    timeZone: 'UTC',
  }).format(new Date(Date.UTC(year, month - 1, day)))
}

export function formatDateInput(date: Date | undefined) {
  if (!date) return undefined

  return new Intl.DateTimeFormat(
    associationDateFormat === 'dmy' ? 'en-GB' : 'en-US',
    associationDateFormat === 'dmy'
      ? { day: '2-digit', month: '2-digit', year: 'numeric' }
      : { day: 'numeric', month: 'short', year: 'numeric' },
  ).format(date)
}

export function displayShortDate(value: string | null) {
  if (!value) return '-'
  const [year, month, day] = value.split('-').map(Number)
  if (!year || !month || !day) return value
  return new Intl.DateTimeFormat(associationDateFormat === 'dmy' ? 'en-GB' : 'en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(new Date(Date.UTC(year, month - 1, day)))
}

export function displayMonthDay(value: string | null) {
  if (!value) return '-'
  const [year, month, day] = value.split('-').map(Number)
  if (!year || !month || !day) return value
  return new Intl.DateTimeFormat(associationDateFormat === 'dmy' ? 'en-GB' : 'en-US', {
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  }).format(new Date(Date.UTC(year, month - 1, day)))
}

export function displayLongDate(value: string | null) {
  if (!value) return '-'
  const [year, month, day] = value.split('-').map(Number)
  if (!year || !month || !day) return value
  return new Intl.DateTimeFormat(associationDateFormat === 'dmy' ? 'en-GB' : 'en-US', {
    dateStyle: 'long',
    timeZone: 'UTC',
  }).format(new Date(Date.UTC(year, month - 1, day)))
}

export function displayMonthYear(value: string | null) {
  if (!value) return '-'
  const [year, month, day] = value.split('-').map(Number)
  if (!year || !month || !day) return value
  return new Intl.DateTimeFormat('en-US', {
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(new Date(Date.UTC(year, month - 1, day)))
}

export function displayShortMonthYear(value: string | null) {
  if (!value) return '-'
  const [year, month, day] = value.split('-').map(Number)
  if (!year || !month || !day) return value
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(new Date(Date.UTC(year, month - 1, day)))
}

export function displayMonth(value: string) {
  const [year, month] = value.split('-').map(Number)
  if (!year || !month) return value
  return new Intl.DateTimeFormat('en-US', { month: 'short', timeZone: 'UTC' }).format(
    new Date(Date.UTC(year, month - 1, 1)),
  )
}

export function displayTime(value: string | null) {
  if (!value) return '-'

  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return value

  return displayTimeFormatter.format(date)
}

export function displayDateTime(value: string | null, timeZone = associationTimeZone) {
  if (!value) return '-'

  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return value

  return displayDateTimeFormatter(timeZone).format(date)
}

export function displayAssociationTime(value: string) {
  return new Intl.DateTimeFormat('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    timeZone: associationTimeZone,
  }).format(new Date(value))
}

function displayDateTimeFormatter(timeZone: string) {
  const cached = displayDateTimeFormatters.get(timeZone)
  if (cached) return cached

  try {
    const formatter = new Intl.DateTimeFormat(associationDateFormat === 'dmy' ? 'en-GB' : 'en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      timeZone,
    })
    displayDateTimeFormatters.set(timeZone, formatter)
    return formatter
  } catch {
    return displayDateTimeFormatter('UTC')
  }
}
